// src/components/AIToolbar.jsx
// AI Assistant greeting toolbar with time-aware greeting + chat input
import React, { useState, useEffect } from 'react';
import { useAI } from '../context/AIContext';
import { Sun, Sunset, Moon, Send, Loader2, Bot } from 'lucide-react';

function getTimeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

function getGreeting() {
  switch (getTimeOfDay()) {
    case 'morning': return 'Good morning';
    case 'afternoon': return 'Good afternoon';
    case 'evening': return 'Good evening';
    default: return 'Hello';
  }
}

function TimeIcon({ className }) {
  switch (getTimeOfDay()) {
    case 'morning': return <Sun className={className} />;
    case 'afternoon': return <Sunset className={className} />;
    case 'evening': return <Moon className={className} />;
    default: return <Sun className={className} />;
  }
}

export default function AIToolbar() {
  const [input, setInput] = useState('');
  const [userName, setUserName] = useState('');
  const { sendMessage, toggleOpen, isStreaming, loading } = useAI();

  useEffect(() => {
    (async () => {
      try {
        const BASE = process.env.REACT_APP_API_URL || '';
        const res = await fetch(`${BASE}/auth/me`, { credentials: 'include' });
        const json = await res.json();
        if (json.ok && json.user) {
          setUserName(json.user.display_name || json.user.name || json.user.email?.split('@')[0] || '');
        }
      } catch {}
    })();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!input.trim() || loading || isStreaming) return;
    const msg = input;
    setInput('');
    await sendMessage(msg);
    toggleOpen();
  }

  return (
    <div className="bg-gradient-to-r from-[#172A3A] via-[#1E3148] to-[#20344A] rounded-xl shadow-lg ring-1 ring-white/10 px-4 sm:px-5 py-3.5">
      <div className="flex items-center gap-4">
        {/* Greeting section */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-md shadow-blue-500/25">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div className="hidden sm:block">
            <div className="flex items-center gap-1.5">
              <TimeIcon className="w-4 h-4 text-yellow-300" />
              <span className="text-white text-base font-semibold">
                {getGreeting()}{userName ? `, ${userName}` : ''}
              </span>
            </div>
            <p className="text-blue-200/80 text-xs mt-0.5">Ask your AI assistant anything</p>
          </div>
        </div>

        {/* Divider */}
        <div className="w-px h-10 bg-white/15 flex-shrink-0 hidden sm:block" />

        {/* Chat input */}
        <form onSubmit={handleSubmit} className="flex items-center gap-2.5 flex-1 min-w-0">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask me anything about your clients, risks, or tasks..."
            className="flex-1 text-sm py-2 px-4 rounded-lg border border-white/20 bg-white/10 text-white placeholder-white/40 focus:bg-white/15 focus:border-blue-400/50 focus:ring-1 focus:ring-blue-400/25 outline-none transition-all"
            disabled={loading || isStreaming}
          />
          <button
            type="submit"
            disabled={!input.trim() || loading || isStreaming}
            className="bg-[#FFB800] text-[#172A3A] rounded-lg px-4 py-2 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-1.5 font-bold text-sm flex-shrink-0 shadow-md shadow-amber-500/20"
          >
            {(loading || isStreaming) ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span className="hidden sm:inline">Ask AI</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
